/**
 * Frame quality assessment (pipeline stage 2, decision half).
 *
 * Quality assessment is the mechanism that makes §20 work. A dark, blurry frame with a tiny
 * face still yields *a* head-pose number, and that number is garbage. Without an explicit
 * gate the system would record it, and the employee would carry the consequences of a badly
 * positioned camera.
 *
 * Pixel measurement itself belongs to the host adapter (it needs a canvas); this module owns
 * the *policy* — which measurements count as unusable, and how much each one should reduce
 * confidence. Keeping the judgement here means it is unit-testable without a DOM.
 */

import {
  combineConfidence,
  faceBoundingBoxRatio,
  type FaceLandmarks,
  type FrameQuality,
  type QualityFlag,
} from '../models.js';
import type { QualityPolicy } from '../config.js';

/** Raw pixel measurements supplied by the host adapter. */
export interface FrameMetrics {
  /** Mean luma, 0-255. */
  readonly brightness: number;
  /** Variance of a Laplacian-style high-pass response; higher means sharper. */
  readonly blurVariance: number;
}

/** Assess a frame before anything is known about faces in it. */
export function assessFrame(metrics: FrameMetrics, policy: QualityPolicy): FrameQuality {
  const flags: QualityFlag[] = [];
  if (metrics.brightness < policy.minBrightness) flags.push('too_dark');
  else if (metrics.brightness > policy.maxBrightness) flags.push('too_bright');
  if (metrics.blurVariance < policy.minBlurVariance) flags.push('blurry');

  return {
    brightness: metrics.brightness,
    blurVariance: metrics.blurVariance,
    faceBoxRatio: null,
    flags,
  };
}

/**
 * Fold face-dependent findings into a frame's quality assessment.
 *
 * Multiple faces deserve special mention: the agent does **not** pick the largest face and
 * score it. Two people in frame means we cannot say whose orientation was measured, so the
 * frame is flagged and the state engine reports `unknown`. Guessing here would attribute a
 * colleague's head movements to the employee — which is precisely the kind of error that
 * would be invisible in aggregate and indefensible in an individual review.
 */
export function assessFace(
  quality: FrameQuality,
  landmarks: FaceLandmarks | null,
  policy: QualityPolicy,
  minLandmarkConfidence: number,
): FrameQuality {
  const flags: QualityFlag[] = [...quality.flags];
  let faceBoxRatio = quality.faceBoxRatio;

  if (landmarks === null) {
    flags.push('no_face');
  } else {
    faceBoxRatio = faceBoundingBoxRatio(landmarks);
    if (landmarks.faceCount > 1) flags.push('multiple_faces');
    if (faceBoxRatio < policy.minFaceBoxRatio) flags.push('face_too_small');
    if (landmarks.detectionConfidence < minLandmarkConfidence) {
      flags.push('low_landmark_confidence');
    }
  }

  return {
    brightness: quality.brightness,
    blurVariance: quality.blurVariance,
    faceBoxRatio,
    flags: dedupe(flags),
  };
}

export function withFlag(quality: FrameQuality, flag: QualityFlag): FrameQuality {
  if (quality.flags.includes(flag)) return quality;
  return { ...quality, flags: [...quality.flags, flag] };
}

/** Quality record for a frame that never arrived. */
export function unavailableQuality(flag: QualityFlag = 'camera_unavailable'): FrameQuality {
  return { brightness: 0, blurVariance: 0, faceBoxRatio: null, flags: [flag] };
}

/**
 * Flags meaning the frame cannot support a measurement at all.
 *
 * The distinction from merely-degraded matters. `too_dark` and `blurry` are here because the
 * thresholds they cross are low bars — brightness below 35/255 is nearly black, and a
 * Laplacian variance below 25 is badly out of focus. A pose solved from such an image may
 * still reproject neatly onto its own bad landmarks and look confident, which is precisely
 * the false precision §20 exists to prevent. Framing problems, by contrast, only reduce
 * certainty.
 */
const DISQUALIFYING_FLAGS: readonly QualityFlag[] = [
  'no_face',
  'camera_unavailable',
  'multiple_faces',
  'too_dark',
  'blurry',
];

const DEGRADING_PENALTIES: Readonly<Partial<Record<QualityFlag, number>>> = {
  too_bright: 0.7,
  face_too_small: 0.6,
  low_landmark_confidence: 0.6,
  high_reprojection_error: 0.6,
};

/**
 * Multiplier applied to inference confidence for degraded frames.
 *
 * Returns 0 for a frame that cannot support a measurement, and otherwise the geometric mean
 * of the applicable penalties — so two mild framing problems reduce certainty rather than
 * compounding into a refusal to conclude anything.
 */
/**
 * Whether a frame is good enough to ask *who* is in it.
 *
 * Separate from `qualityConfidenceFactor` because the two questions differ in kind: a degraded
 * frame can still support a hedged orientation reading, but there is no such thing as a hedged
 * identity comparison — the embedding either describes the face or it describes noise, and a
 * noisy embedding scores low against every template, which reads as "a different person".
 *
 * This exists because of a real misdiagnosis: an obscured camera produced a detectable but
 * unusable face, the embedding scored 0.343, and the session locked reporting
 * `identity_mismatch`. The employee had covered the lens, not swapped places with somebody —
 * and the log accused them of the latter. `multiple_faces` is included for the mirror-image
 * reason: with two people in shot the nearest face is measured, so a confident *match* can be a
 * correct answer to the wrong question.
 */
export function isUsableForIdentity(quality: FrameQuality | null): boolean {
  if (quality === null) return false;
  return !DISQUALIFYING_FLAGS.some((flag) => quality.flags.includes(flag));
}

export function qualityConfidenceFactor(quality: FrameQuality): number {
  for (const flag of DISQUALIFYING_FLAGS) {
    if (quality.flags.includes(flag)) return 0;
  }
  const penalties = quality.flags
    .map((flag) => DEGRADING_PENALTIES[flag])
    .filter((penalty): penalty is number => penalty !== undefined);
  return penalties.length === 0 ? 1 : combineConfidence(...penalties);
}

/**
 * Employee-facing explanations for each quality flag.
 *
 * These strings surface in event evidence and in the preview overlay. They name the *camera
 * condition*, never the person — "camera image is dark", never anything that reads as a
 * judgement about the employee.
 */
const FLAG_MESSAGES: Readonly<Record<QualityFlag, string>> = {
  too_dark: 'camera image is dark; orientation is less reliable',
  too_bright: 'camera image is over-exposed; orientation is less reliable',
  blurry: 'camera image is blurry or out of focus',
  face_too_small: 'face is small in frame (subject may be far from the camera)',
  multiple_faces: 'more than one face in frame; orientation cannot be attributed to one person',
  no_face: 'no face visible to the camera',
  low_landmark_confidence: 'facial landmarks were detected weakly',
  high_reprojection_error: 'head geometry did not fit the model well',
  camera_unavailable: 'camera is unavailable',
};

export function describeFlags(quality: FrameQuality): string[] {
  return quality.flags.map((flag) => FLAG_MESSAGES[flag]);
}

function dedupe(flags: readonly QualityFlag[]): QualityFlag[] {
  return [...new Set(flags)];
}
